import { ProductJourney } from "@shared/analytics/productEvents";
import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { AccountTypeBadge } from "@shared/components/Yaas/AccountTypeBadge";
import { truncateAddress } from "@shared/utils/format";
import { ArrowShortLeftIcon } from "@solomon-labs/design/components/icons";
import { SectionHeading } from "@solomon-labs/design/components/layout";
import { toastManager } from "@solomon-labs/design/components/ui";
import {
    Button,
    Copy,
    NumberInput,
    NumberInputAddon,
    NumberInputField,
    NumberInputGroup,
    Text
} from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import React, { useRef, useState } from "react";

import { usePositionSplits } from "@/hooks/yaas";

import { LabeledRewardsDestination } from "../application/LabeledRewardsDestination";
import type { SharedDestination } from "../state/types";

import { AddDestinationForm } from "./AddDestinationForm";

/**
 * Per-position Route Rewards Destinations surface. Triggered by the
 * "Destinations" / "Update" link beside a position's breakdown row.
 * Per Model E in the routing research doc, each position owns its own
 * routing config — there's no global state object in play here.
 *
 * Two visual modes:
 *   - Mode 2 (simple, advancedMode=false): single-destination form
 *   - Mode 3 (advanced, advancedMode=true): multi-destination splits
 *     reading/writing the shared `destinations` list lifted from
 *     EarnContent. Adding a destination in the Reward Destinations
 *     panel immediately shows up here.
 */
export function PerPositionRoutingPanel({
    analytics: entryAnalytics,
    positionSource,
    advancedMode,
    destinations,
    setDestinations,
    simpleRoutingLabel,
    setSimpleRoutingLabel,
    simpleRoutingAddress,
    setSimpleRoutingAddress,
    connectedWallet,
    walletAddress,
    positionId,
    onCreateDestination,
    isCreatingDestination,
    onClose
}: {
    analytics?: ProductJourney;
    /** The position name shown in the trailing slot of the section
     *  heading — scopes the routing config visually. */
    positionSource: string;
    advancedMode: boolean;
    destinations: SharedDestination[];
    setDestinations: React.Dispatch<React.SetStateAction<SharedDestination[]>>;
    simpleRoutingLabel: string;
    setSimpleRoutingLabel: (next: string) => void;
    simpleRoutingAddress: string;
    setSimpleRoutingAddress: (next: string) => void;
    connectedWallet: string;
    walletAddress: string;
    positionId: number | null;
    /** Signed create of a saved destination. Powers the in-panel
     *  "+Add Destination" form so a user can add an address without
     *  leaving the routing screen. */
    onCreateDestination?: (input: {
        name: string;
        address: string;
        analytics?: ProductJourney;
    }) => Promise<
        | { success: true; destination: SharedDestination }
        | { success: false; error: string }
    >;
    isCreatingDestination?: boolean;
    onClose: () => void;
}): React.ReactElement {
    const localAnalytics = useProductJourney(
        "rewards_routing",
        "position_row",
        walletAddress
    );
    const analytics = entryAnalytics ?? localAnalytics;
    useProductObservation(analytics, "form_viewed", { advanced: advancedMode });
    return (
        <motion.div
            key="route-destinations"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{
                opacity: { duration: 0.3, ease: "easeOut" },
                y: { duration: 0.3, ease: "easeOut" }
            }}
            className="flex flex-col gap-3"
        >
            <Button
                variant="link"
                size="sm"
                onClick={() => {
                    analytics.close("back");
                    onClose();
                }}
                className="self-start"
            >
                <ArrowShortLeftIcon className="size-4" />
                Back
            </Button>
            {/* SectionHeading's `trailing` slot names the position this
                routing config is scoped to — inline with the title,
                above the same divider as the heading. */}
            <SectionHeading
                variant="h1"
                trailing={
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-neutral"
                    >
                        {positionSource}
                    </Text>
                }
            >
                Rewards Routing
            </SectionHeading>
            {advancedMode ? (
                <AdvancedRoutingForm
                    analytics={analytics}
                    walletAddress={walletAddress}
                    positionId={positionId}
                    destinations={destinations}
                    setDestinations={setDestinations}
                    onCreateDestination={onCreateDestination}
                    isCreatingDestination={isCreatingDestination}
                    onClose={onClose}
                />
            ) : (
                // Mode 2 — simple single-destination form: optional
                // label input + address field. No splits, no
                // percentages, no SectionHeading.
                <div className="mt-3">
                    <LabeledRewardsDestination
                        label={simpleRoutingLabel}
                        onLabelChange={setSimpleRoutingLabel}
                        address={simpleRoutingAddress}
                        onAddressChange={setSimpleRoutingAddress}
                        connectedWallet={connectedWallet}
                        labelPlaceholder="Account Label (Optional)"
                        showDestinationLabel={false}
                    />
                    <Button
                        variant="raised"
                        interaction="light-bar"
                        size="lg"
                        fullWidth
                        className="mt-15"
                        onClick={onClose}
                    >
                        Verify Ownership to Update
                    </Button>
                </div>
            )}
        </motion.div>
    );
}

function AdvancedRoutingForm({
    analytics,
    walletAddress,
    positionId,
    destinations,
    setDestinations,
    onCreateDestination,
    isCreatingDestination,
    onClose
}: {
    analytics: ProductJourney;
    walletAddress: string;
    positionId: number | null;
    destinations: SharedDestination[];
    setDestinations: React.Dispatch<React.SetStateAction<SharedDestination[]>>;
    onCreateDestination?: (input: {
        name: string;
        address: string;
        analytics?: ProductJourney;
    }) => Promise<
        | { success: true; destination: SharedDestination }
        | { success: false; error: string }
    >;
    isCreatingDestination?: boolean;
    onClose: () => void;
}): React.ReactElement {
    const positionSplits = usePositionSplits();
    // Collapsed by default — the panel's job is allocating percentages;
    // adding an address is the occasional side-errand that used to
    // require leaving the screen entirely.
    const [isAddingDestination, setIsAddingDestination] = useState(false);
    const destinationJourney = useRef<ProductJourney | undefined>(undefined);
    // Every destination renders, including ones with no `uuid` — the
    // synthetic source row, and any live destination that was never
    // saved to the address book. Those used to be filtered out, which
    // is why a wallet with nothing saved saw an empty grid while 100%
    // was demonstrably routing somewhere.
    //
    // They are shown but NOT allocatable: splits submit
    // `savedDestinationUuid`, and a row without one cannot be part of
    // the payload. "+Add Destination" is the path to making it
    // allocatable.
    const allocatableDestinations = destinations.filter(
        (destination): destination is SharedDestination & { uuid: string } =>
            !!destination.uuid
    );
    const routingTotal = allocatableDestinations.reduce(
        (s, d) => s + d.percentage,
        0
    );
    const routingInvalid = routingTotal !== 100;
    const activeDestinations = allocatableDestinations.filter(
        (d) => d.percentage > 0
    );
    const canSubmit =
        !routingInvalid &&
        activeDestinations.length > 0 &&
        !!walletAddress &&
        positionId !== null &&
        !positionSplits.isLoading;
    useProductObservation(analytics, "validation_updated", {
        can_submit: canSubmit,
        destination_count: activeDestinations.length,
        allocation_total_bps: Math.round(routingTotal * 100)
    });
    const setRowPercentage = (id: string, n: number): void => {
        analytics.send("changed", { control: "percentage" });
        // Constrain to whole percentages — the backend stores basis points
        // (percentage * 100) and rejects non-integer bps, so a fractional
        // entry like 33.33 would 400 on submit.
        const clamped = Math.max(0, Math.min(100, Math.round(n)));
        setDestinations((prev) =>
            prev.map((d) =>
                d.uuid && d.id === id ? { ...d, percentage: clamped } : d
            )
        );
    };
    const allToRow = (id: string): void => {
        analytics.send("changed", { control: "all" });
        setDestinations((prev) =>
            prev.map((d) =>
                d.uuid ? { ...d, percentage: d.id === id ? 100 : 0 } : d
            )
        );
    };
    const splitEvenly = (): void => {
        analytics.send("changed", { control: "split_evenly" });
        setDestinations((prev) => {
            const n = prev.filter((d) => d.uuid).length;
            if (n === 0) return prev;
            const even = Math.floor(100 / n);
            const remainder = 100 - even * n;
            let savedIndex = 0;
            return prev.map((d) => {
                if (!d.uuid) return d;
                const percentage = savedIndex === 0 ? even + remainder : even;
                savedIndex += 1;
                return { ...d, percentage };
            });
        });
    };
    const clearRouting = (): void => {
        analytics.send("changed", { control: "clear" });
        setDestinations((prev) =>
            prev.map((d) => (d.uuid ? { ...d, percentage: 0 } : d))
        );
    };
    const submitSplits = async (): Promise<void> => {
        if (!canSubmit || positionId === null) return;
        const result = await positionSplits.setPositionSplits({
            analytics,
            walletAddress,
            positionId,
            splits: activeDestinations.map((destination) => ({
                savedDestinationUuid: destination.uuid,
                percentageBps: Math.round(destination.percentage * 100)
            }))
        });
        if (!result.success) {
            toastManager.add({
                title: "Rewards routing was not updated",
                description: result.error,
                data: { variant: "error" }
            });
            return;
        }
        toastManager.add({
            title: "Rewards routing updated",
            data: { variant: "success" }
        });
        analytics.close("completed");
        onClose();
    };

    return (
        <>
            <div className="mt-3">
                <div className="flex flex-col gap-2">
                    {destinations.map((d) => (
                        <div
                            key={d.id}
                            className="flex items-center justify-between gap-3"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <AccountTypeBadge
                                    type={d.type}
                                    className="shrink-0"
                                />
                                {/* DS Copy element — owns the
                                    copy→checkmark swap, the 1.5s
                                    feedback window and reduced-motion
                                    handling. Copies the FULL address
                                    even though the row renders a
                                    truncated one. */}
                                <Copy
                                    content={d.address}
                                    onClick={() =>
                                        analytics.send("copy_requested", {
                                            control: "destination_address"
                                        })
                                    }
                                    copyLabel={`Copy ${d.address}`}
                                    className="min-w-0"
                                >
                                    <Text
                                        variant="number"
                                        render={<span />}
                                        className="text-foreground-base min-w-0 truncate"
                                    >
                                        {/* Saved rows carry a name; an
                                            unsaved one is identified by
                                            the address it is currently
                                            configured to. */}
                                        {(d.uuid && d.label) ||
                                            truncateAddress(d.address, 6, 6)}
                                    </Text>
                                </Copy>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {/* NumberInput rather than InputGroup:
                                    `NumberInputGroup`'s `size` propagates
                                    to its addon, where `InputGroupAddon`
                                    hardcodes h-10 — so the "%" was a 40px
                                    box inside a 32px shell. Base UI also
                                    renders the field as type="text", so
                                    the spinner-suppression arbitrary
                                    values are no longer needed.

                                    Width is pinned to fit "100%", the
                                    widest value the field can hold.

                                    `state` is only ever "error" on an
                                    allocatable row: `routingInvalid`
                                    measures the total across rows that
                                    CAN be allocated, so flagging a
                                    disabled unsaved row with it painted
                                    an error on a box reading 100% purely
                                    because that row isn't counted. */}
                                <NumberInput
                                    /* Width belongs on the root: it is
                                       `inline-flex` with no width and no
                                       shrink-0. Setting it on the field
                                       does nothing either — that has
                                       `min-w-0 flex-1`, so it shrinks
                                       past any width class (it rendered
                                       31px while carrying w-24). */
                                    className="w-16 shrink-0"
                                    value={d.percentage}
                                    onValueChange={(next) =>
                                        setRowPercentage(d.id, next ?? 0)
                                    }
                                    min={0}
                                    max={100}
                                    step={1}
                                    disabled={!d.uuid}
                                >
                                    <NumberInputGroup
                                        size="sm"
                                        controls="none"
                                        state={
                                            d.uuid && routingInvalid
                                                ? "error"
                                                : undefined
                                        }
                                        /* px on the combined field, gap
                                           between its two parts. This
                                           only works because the
                                           children's px is zeroed on the
                                           base AND the size variant —
                                           otherwise theirs stacks on top
                                           of this and clips the value. */
                                        className="px-2 gap-1"
                                    >
                                        {/* Both children carry px-3 AND a
                                            `group-data-[size=sm]:px-2.5`
                                            variant. tailwind-merge keys
                                            variants separately, so a bare
                                            px-0 only beats the base — the
                                            variant has to be zeroed by
                                            name or the padding survives. */}
                                        <NumberInputField className="px-0 group-data-[size=sm]/number-input-group:px-0 text-right" />
                                        <NumberInputAddon
                                            align="inline-end"
                                            className="px-0 group-data-[size=sm]/number-input-group:px-0"
                                        >
                                            %
                                        </NumberInputAddon>
                                    </NumberInputGroup>
                                </NumberInput>
                                <Button
                                    variant="link"
                                    size="sm"
                                    disabled={!d.uuid}
                                    onClick={() => allToRow(d.id)}
                                >
                                    All
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-4 flex justify-between gap-2">
                    {/* Left slot — adding a destination is a different
                        kind of action from Split Evenly / Clear (which
                        only rearrange what is already here), so it sits
                        apart from them rather than in the same cluster. */}
                    {/* Hidden while the form is open — Cancel lives in
                        the form's own action row, so leaving this here
                        too would be two controls for one job. */}
                    {onCreateDestination && !isAddingDestination ? (
                        <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                                destinationJourney.current = new ProductJourney(
                                    "saved_destination",
                                    "routing_panel",
                                    walletAddress
                                );
                                destinationJourney.current.send("requested");
                                setIsAddingDestination(true);
                            }}
                        >
                            +Add Destination
                        </Button>
                    ) : (
                        <span />
                    )}
                    <div className="flex gap-2">
                        <Button variant="link" size="sm" onClick={splitEvenly}>
                            Split Evenly
                        </Button>
                        <Button variant="link" size="sm" onClick={clearRouting}>
                            Clear
                        </Button>
                    </div>
                </div>
                {isAddingDestination && onCreateDestination && (
                    <AddDestinationForm
                        analytics={destinationJourney.current}
                        /* Mirror the grid, not the raw list. `destinations`
                       also carries a synthetic "Source Account" row with
                       no uuid (fallback for a wallet not yet seeded into
                       the book); the grid filters those out, so counting
                       them here reported "already added" for an address
                       that had no row — and blocked adding the very
                       entry that would create one. */
                        existingAddresses={allocatableDestinations.map(
                            (d) => d.address
                        )}
                        isCreating={!!isCreatingDestination}
                        onCreate={onCreateDestination}
                        onAdded={() => setIsAddingDestination(false)}
                        onCancel={() => setIsAddingDestination(false)}
                    />
                )}
            </div>
            <Button
                variant="raised"
                interaction="light-bar"
                size="lg"
                fullWidth
                className="mt-15"
                disabled={!canSubmit}
                onClick={() => {
                    void submitSplits();
                }}
            >
                {positionSplits.isLoading
                    ? "Updating..."
                    : "Verify Ownership to Update"}
            </Button>
        </>
    );
}
